/**
 * The institution experience layer — vocabulary and capability packs.
 *
 * Both live in campusone-core-service under src/institution-experience/** and both
 * answer the same kind of question: "what should THIS institution's screens say and
 * show?", resolved from layered defaults the institution can override. The shapes
 * here mirror the service's interfaces field for field; nothing in this file is a
 * view model.
 */

/* ─── Terminology ─── */

/**
 * The 13 canonical concepts the platform names.
 *
 * Transcribed from CanonicalConcept in core's terminology domain. These are the
 * KEYS of the vocabulary, not the words: LEARNER may resolve to "Student", "Pupil"
 * or "Élève" depending on institution type, subsystem and locale. The editor
 * renders exactly this list — a concept core mints later is deliberately not shown
 * rather than shown without a label explaining what it names.
 */
export const CANONICAL_CONCEPTS = [
  'LEARNER',
  'TEACHING_GROUP',
  'LEARNING_COMPONENT',
  'ACADEMIC_PERIOD',
  'TEACHER',
  'RESULT_DOCUMENT',
  'ACADEMIC_UNIT',
  'PROGRAMME_OF_STUDY',
  'ACADEMIC_YEAR',
  'ASSESSMENT',
  'GUARDIAN',
  'HEAD_OF_INSTITUTION',
  'CREDENTIAL',
] as const;

export type CanonicalConcept = (typeof CANONICAL_CONCEPTS)[number];

/**
 * The resolver's ladder, most specific first — core's TERMINOLOGY_SCOPE_PRECEDENCE.
 *
 * 'PLATFORM_DEFAULT' is not a scope: it is the source a term reports when no stored
 * label spoke at any scope and the in-code floor answered.
 */
export const TERMINOLOGY_SCOPES = [
  'CAMPUS',
  'INSTITUTION',
  'SUBSYSTEM',
  'INSTITUTION_TYPE',
  'COUNTRY',
  'PLATFORM',
] as const;

export type TerminologyScope = (typeof TERMINOLOGY_SCOPES)[number];

export type TermSource = TerminologyScope | 'PLATFORM_DEFAULT';

/** One resolved word: what the screens should print for a concept. */
export interface ResolvedTerm {
  singular: string;
  plural: string;
  /** Never empty — the service falls back to the singular. */
  short: string;
  canonicalKey: CanonicalConcept;
  /** The layer that actually supplied the word — the provenance the editor shows. */
  source: TermSource;
  /** The locale the winning label was written in, which may be a fallback of the one asked for. */
  locale: string;
}

export interface ResolvedTerminology {
  institutionId: string;
  campusId: string | null;
  /** The locale actually served, normalised — asking for fr_cm comes back as fr-CM. */
  locale: string;
  /** The layers consulted, most specific first. Diagnostic; the editor prints it as provenance help. */
  layers: { scope: TerminologyScope; scopeRef: string | null }[];
  terms: Partial<Record<CanonicalConcept, ResolvedTerm>>;
}

/**
 * One stored override row — what GET .../terminology/overrides lists and PUT returns.
 * Field names are the entity's (singularLabel), not the resolved term's (singular).
 */
export interface TerminologyOverride {
  id: string;
  canonicalKey: CanonicalConcept;
  scope: TerminologyScope;
  scopeRef: string | null;
  locale: string;
  singularLabel: string;
  pluralLabel: string;
  shortLabel: string | null;
  updatedBy: string | null;
}

/** PUT body — mirrors WriteTerminologyLabelDto. An extra field is a 400 (forbidNonWhitelisted). */
export interface WriteTerminologyLabel {
  locale: string;
  singularLabel: string;
  pluralLabel: string;
  shortLabel?: string;
  /** Present switches the write to CAMPUS scope. Absent writes the institution's own layer. */
  campusId?: string;
}

/* ─── Capability packs ─── */

/**
 * The 10 packs core's catalogue ships. A mirror for typing and ordering; the card
 * grid renders whatever the catalogue endpoint actually returns, so a pack minted
 * later still appears — typed as string, labelled by its own title field.
 */
export const CAPABILITY_PACKS = [
  'SCHOOL_ACADEMIC_CORE',
  'UNIVERSITY_CREDIT_SYSTEM',
  'VOCATIONAL_COMPETENCY_SYSTEM',
  'HEALTH_CLINICAL_ROTATIONS',
  'BOARDING_AND_HOSTEL',
  'SCHOOL_TRANSPORT',
  'LIBRARY_OPERATIONS',
  'PLACEMENT_OPERATIONS',
  'RESEARCH_AND_POSTGRADUATE',
  'BILINGUAL_CAMEROON',
] as const;

export type CapabilityPackCode = string;

/** One catalogue entry — GET /experience/capability-packs. `summary`, not `description`. */
export interface CapabilityPackDefinition {
  pack: CapabilityPackCode;
  title: string;
  summary: string;
  capabilities: string[];
  modules: string[];
}

/**
 * How a pack came to be on or off here. The distinction is the audit story:
 *   DEFAULT   — on because the institution's type says so; nobody here decided anything.
 *   ADDED     — the institution switched on a pack its type does not default to.
 *   REMOVED   — the institution switched off a pack its type defaults to.
 *   CONFIRMED — the institution recorded a decision that happens to match the default.
 */
export type PackSource = 'DEFAULT' | 'ADDED' | 'REMOVED' | 'CONFIRMED';

export const PACK_SOURCES: readonly PackSource[] = ['DEFAULT', 'ADDED', 'REMOVED', 'CONFIRMED'];

export interface ResolvedPack {
  pack: CapabilityPackCode;
  title: string;
  active: boolean;
  source: PackSource;
  note: string | null;
}

/** GET/PUT/DELETE .../experience/capability-packs all answer with this full recomputed set. */
export interface ResolvedCapabilitySet {
  institutionId: string;
  institutionType: string | null;
  educationSubsystem: string | null;
  /** Active pack codes only. */
  packs: CapabilityPackCode[];
  /** Every pack, active or not, in catalogue order — the card grid's data. */
  packDetail: ResolvedPack[];
  /** The union over active packs, de-duplicated. */
  capabilities: string[];
  modules: string[];
  warnings: string[];
}
